import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, Loader2, AlertCircle, X, Store, Tag, CheckCircle, XCircle
} from 'lucide-react';
import { useReservation, ReservationProvider } from '../contexts/ReservationContext';
import { useVendor } from '../contexts/VendorContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { slotApi, fairApi, discountCodeApi } from '../services/api';
import { Fair, Slot, SlotAvailabilityCheck, ReservationRentalItemCreate } from '../types';
import { formatCurrency } from '../utils/currency';
import ReservationTypeSelector from './ReservationTypeSelector';
import DateSelectionCalendar from './DateSelectionCalendar';
import RentalItemSelector from './RentalItemSelector';
import ReservationSummary from './ReservationSummary';

// Step definitions for reference (step numbers are dynamic based on reservation type)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const STEPS = [
  { id: 1, title: 'Select Vendor', description: 'Choose your vendor profile' },
  { id: 2, title: 'Reservation Type', description: 'Full duration or per-day' },
  { id: 3, title: 'Select Dates', description: 'Choose your dates' },
  { id: 4, title: 'Rental Items', description: 'Add optional equipment' },
  { id: 5, title: 'Special Requests', description: 'Add any notes' },
  { id: 6, title: 'Review & Confirm', description: 'Confirm your booking' },
];

function ReservationWizardContent() {
  const { slotId } = useParams<{ slotId: string }>();
  const navigate = useNavigate();

  const {
    flow,
    updateFlow,
    resetFlow,
    calculatePrice,
    checkAvailability,
    submitReservation,
    isLoading,
    error,
    clearError,
  } = useReservation();

  const { vendors, loadVendors } = useVendor();

  // Issue #1469: Check if discount codes feature is enabled
  const { isEnabled: discountCodesEnabled } = useFeatureFlag('discount_codes');

  const [slot, setSlot] = useState<Slot | null>(null);
  const [fair, setFair] = useState<Fair | null>(null);
  const [availability, setAvailability] = useState<SlotAvailabilityCheck | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const flowInitializedRef = useRef(false);

  // Discount code validation state (Issue #375, #1469)
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [discountValidationMessage, setDiscountValidationMessage] = useState<string | null>(null);

  // Load slot and fair data
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!slotId) {
        setLoadError('Invalid slot ID');
        setLoadingData(false);
        return;
      }

      const id = parseInt(slotId, 10);
      if (isNaN(id)) {
        setLoadError('Invalid slot ID');
        setLoadingData(false);
        return;
      }

      try {
        setLoadingData(true);
        setLoadError(null);

        // Load slot details
        const slotData = await slotApi.getSlotDetails(id);
        if (!isMounted) return;
        setSlot(slotData);

        // Load fair details using the proper API client
        const fairData = await fairApi.getById(slotData.fair_id);
        if (!isMounted) return;
        setFair(fairData);

        // Load vendors
        await loadVendors();

        // Check initial availability
        const availabilityData = await checkAvailability(id, 'full_duration');
        if (!isMounted) return;
        setAvailability(availabilityData);

      } catch (err: unknown) {
        console.error('Failed to load reservation data:', err);
        if (isMounted) {
          // Provide specific error messages based on error type
          const errorMessage = err.response?.data?.detail ||
            err.response?.status === 404 ? 'Slot or fair not found. It may have been removed.' :
            err.response?.status === 401 ? 'Please log in to make a reservation.' :
            err.response?.status === 403 ? 'You do not have permission to reserve this slot.' :
            err.message || 'Unable to load slot information. Please try again later.';
          setLoadError(errorMessage);
        }
      } finally {
        if (isMounted) {
          setLoadingData(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [slotId, checkAvailability, loadVendors]);

  // Initialize flow when data is loaded (only once)
  useEffect(() => {
    if (slot && fair && vendors.length > 0 && !flowInitializedRef.current) {
      flowInitializedRef.current = true;
      updateFlow({
        slot,
        fair,
        vendor_id: vendors[0].id,
        step: 1,
      });
    }
  }, [slot, fair, vendors, updateFlow]);

  // Update price when flow changes (Issue #375 - also track original price)
  useEffect(() => {
    if (!slot || !fair) return;

    const basePrice = slot.base_price;
    let originalPrice = 0;

    if (flow.reservation_type === 'full_duration') {
      if (fair.start_date && fair.end_date) {
        const [startYear, startMonth, startDay] = fair.start_date.split('-').map(Number);
        const [endYear, endMonth, endDay] = fair.end_date.split('-').map(Number);
        const startDate = Date.UTC(startYear, startMonth - 1, startDay);
        const endDate = Date.UTC(endYear, endMonth - 1, endDay);
        const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
        originalPrice = basePrice * days;
      } else {
        originalPrice = basePrice;
      }
    } else if (flow.reservation_type === 'per_day') {
      originalPrice = basePrice * flow.selected_dates.length;
    }

    const finalPrice = calculatePrice();

    if (finalPrice !== flow.total_price || originalPrice !== flow.original_price) {
      updateFlow({ total_price: finalPrice, original_price: originalPrice });
    }
  }, [slot, fair, flow.reservation_type, flow.selected_dates, flow.discount_valid, flow.discount_type, flow.discount_value, calculatePrice, flow.total_price, flow.original_price, updateFlow]);

  // Validate discount code (Issue #375)
  const handleValidateDiscount = useCallback(async () => {
    if (!flow.discount_code || !slot) {
      return;
    }

    try {
      setValidatingDiscount(true);
      setDiscountValidationMessage(null);

      const result = await discountCodeApi.validateDiscountCode({
        code: flow.discount_code,
        slot_id: slot.id,
      });

      if (result.is_valid && result.discount_type && result.discount_value !== null && result.discount_value !== undefined) {
        // Update flow with valid discount
        updateFlow({
          discount_valid: true,
          discount_type: result.discount_type,
          discount_value: result.discount_value,
        });
        setDiscountValidationMessage(result.message || 'Discount code applied successfully!');
      } else {
        // Invalid code
        updateFlow({
          discount_valid: false,
          discount_type: null,
          discount_value: null,
        });
        setDiscountValidationMessage(result.message || 'Invalid discount code');
      }
    } catch (err: unknown) {
      console.error('Failed to validate discount code:', err);
      updateFlow({
        discount_valid: false,
        discount_type: null,
        discount_value: null,
      });
      setDiscountValidationMessage('Unable to validate discount code. Please try again.');
    } finally {
      setValidatingDiscount(false);
    }
  }, [flow.discount_code, slot, updateFlow]);

  const handleNext = useCallback(async () => {
    const currentStep = flow.step;

    // Validation for each step
    if (currentStep === 1 && !flow.vendor_id) {
      return;
    }

    if (currentStep === 2 && !flow.reservation_type) {
      return;
    }

    if (currentStep === 3 && flow.reservation_type === 'per_day' && flow.selected_dates.length === 0) {
      return;
    }

    // Skip date selection for full_duration
    if (currentStep === 2 && flow.reservation_type === 'full_duration') {
      updateFlow({ step: 4 }); // Skip to rental items step
      return;
    }

    // Final step - submit
    if (currentStep === 6) {
      try {
        const reservation = await submitReservation();
        navigate(`/reservation-confirmed?id=${reservation.id}`);
      } catch {
        // Error handled by context
      }
      return;
    }

    updateFlow({ step: currentStep + 1 });
  }, [flow, updateFlow, submitReservation, navigate]);

  const handleBack = useCallback(() => {
    const currentStep = flow.step;

    // Skip date selection for full_duration when going back from rental items
    if (currentStep === 4 && flow.reservation_type === 'full_duration') {
      updateFlow({ step: 2 });
      return;
    }

    if (currentStep > 1) {
      updateFlow({ step: currentStep - 1 });
    }
  }, [flow, updateFlow]);

  const handleExit = () => {
    if (flow.step > 1) {
      setShowExitConfirm(true);
    } else {
      navigate(-1);
    }
  };

  const confirmExit = () => {
    resetFlow();
    navigate(-1);
  };

  const handleDateToggle = (date: string) => {
    const newDates = flow.selected_dates.includes(date)
      ? flow.selected_dates.filter(d => d !== date)
      : [...flow.selected_dates, date];
    updateFlow({ selected_dates: newDates });
  };

  // Calculate current step for display (accounting for skipped steps)
  const getDisplayStep = () => {
    if (flow.reservation_type === 'full_duration' && flow.step >= 4) {
      return flow.step - 1; // Skip date selection step in display
    }
    return flow.step;
  };

  const getTotalSteps = () => {
    return flow.reservation_type === 'full_duration' ? 5 : 6;
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (loadError || !slot || !fair) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12 bg-red-50 rounded-lg">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {loadError || 'Failed to load slot'}
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="text-secondary hover:text-primary-dark font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={handleExit}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Exit
        </button>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Reserve Slot</h1>
          <p className="text-gray-600 text-sm">Slot #{slot.slot_number} at {fair.name}</p>
        </div>
        <div className="w-16" /> {/* Spacer for alignment */}
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {Array.from({ length: getTotalSteps() }, (_, i) => i + 1).map((stepNum) => (
            <React.Fragment key={stepNum}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    getDisplayStep() > stepNum
                      ? 'bg-teal-500 text-white'
                      : getDisplayStep() === stepNum
                      ? 'bg-primary text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {getDisplayStep() > stepNum ? <Check className="w-5 h-5" /> : stepNum}
                </div>
                <span className="text-xs mt-1 text-gray-500 hidden sm:block">
                  Step {stepNum}
                </span>
              </div>
              {stepNum < getTotalSteps() && (
                <div
                  className={`flex-1 h-1 mx-2 rounded ${
                    getDisplayStep() > stepNum ? 'bg-teal-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-700">{error}</p>
          </div>
          <button
            onClick={clearError}
            aria-label="Dismiss error"
            className="text-red-500 hover:text-red-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        {/* Step 1: Select Vendor */}
        {flow.step === 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Select Vendor Profile</h3>
            <p className="text-gray-600 text-sm">
              Choose which vendor profile to use for this reservation.
            </p>

            {vendors.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Store className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">You don't have any vendor profiles yet.</p>
                <button
                  onClick={() => navigate('/vendor/profile/new')}
                  className="text-secondary hover:text-primary-dark font-medium"
                >
                  Create a vendor profile
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {vendors.map(vendor => (
                  <button
                    key={vendor.id}
                    onClick={() => updateFlow({ vendor_id: vendor.id })}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      flow.vendor_id === vendor.id
                        ? 'border-primary bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                        {vendor.portfolio_images?.[0] ? (
                          <img
                            src={vendor.portfolio_images[0]}
                            alt=""
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <Store className="w-6 h-6 text-secondary" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{vendor.business_name}</p>
                        {vendor.product_types && vendor.product_types.length > 0 && (
                          <p className="text-sm text-gray-500">
                            {vendor.product_types.slice(0, 3).join(', ')}
                          </p>
                        )}
                      </div>
                      {flow.vendor_id === vendor.id && (
                        <Check className="w-5 h-5 text-secondary ml-auto" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Reservation Type */}
        {flow.step === 2 && (
          <ReservationTypeSelector
            slot={slot}
            fair={fair}
            selectedType={flow.reservation_type}
            onSelect={(type) => updateFlow({ reservation_type: type, selected_dates: [] })}
            disabled={isLoading}
          />
        )}

        {/* Step 3: Date Selection (per-day only) */}
        {flow.step === 3 && flow.reservation_type === 'per_day' && (
          <DateSelectionCalendar
            slot={slot}
            fair={fair}
            selectedDates={flow.selected_dates}
            unavailableDates={availability?.unavailable_dates || []}
            onDateToggle={handleDateToggle}
            disabled={isLoading}
          />
        )}

        {/* Step 4: Rental Items */}
        {flow.step === 4 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Equipment Rentals</h3>
            <p className="text-gray-600 text-sm">
              Add optional rental equipment to your reservation.
            </p>
            <RentalItemSelector
              fairId={fair.id}
              selectedItems={flow.rental_items}
              onItemsChange={(items: ReservationRentalItemCreate[]) => updateFlow({ rental_items: items })}
            />
          </div>
        )}

        {/* Step 5: Special Requests & Discount Code */}
        {flow.step === 5 && (
          <div className="space-y-6">
            {/* Discount Code Section (Issue #375, #1469) */}
            {discountCodesEnabled && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Tag className="w-5 h-5 text-secondary" />
                  <h3 className="text-lg font-semibold text-gray-900">Discount Code</h3>
                  <span className="text-sm text-gray-500">(Optional)</span>
                </div>
                <p className="text-gray-600 text-sm">
                  Have a discount code? Enter it below to apply a discount to your reservation.
                </p>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={flow.discount_code || ''}
                  onChange={(e) => {
                    const code = e.target.value.toUpperCase();
                    updateFlow({
                      discount_code: code,
                      discount_valid: false, // Reset validation when code changes
                      discount_type: null,
                      discount_value: null,
                    });
                    setDiscountValidationMessage(null);
                  }}
                  placeholder="Enter discount code"
                  maxLength={50}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary uppercase"
                />
                <button
                  onClick={handleValidateDiscount}
                  disabled={!flow.discount_code || validatingDiscount || flow.discount_valid}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {validatingDiscount ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Validating...
                    </>
                  ) : flow.discount_valid ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Applied
                    </>
                  ) : (
                    'Validate'
                  )}
                </button>
              </div>

              {/* Validation Message */}
              {discountValidationMessage && (
                <div
                  className={`p-3 rounded-lg flex items-start gap-2 ${
                    flow.discount_valid
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}
                >
                  {flow.discount_valid ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <p
                    className={`text-sm ${
                      flow.discount_valid ? 'text-green-700' : 'text-red-700'
                    }`}
                  >
                    {discountValidationMessage}
                  </p>
                </div>
              )}

              {/* Discount Details */}
              {flow.discount_valid && flow.discount_type && flow.discount_value !== null && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm font-medium text-green-900 mb-1">
                    Discount Applied:
                  </p>
                  <p className="text-lg font-semibold text-green-700">
                    {flow.discount_type === 'percentage' && `${flow.discount_value}% off`}
                    {flow.discount_type === 'fixed_amount' && `${formatCurrency(String(flow.discount_value))} off`} {/* Issue #905: Use formatCurrency for precision */}
                    {flow.discount_type === 'free' && 'Free reservation!'}
                  </p>
                  <button
                    onClick={() => {
                      updateFlow({
                        discount_code: null,
                        discount_valid: false,
                        discount_type: null,
                        discount_value: null,
                      });
                      setDiscountValidationMessage(null);
                    }}
                    className="text-sm text-green-700 hover:text-green-900 underline mt-2"
                  >
                    Remove discount
                  </button>
                </div>
              )}
              </div>
            )}

            {/* Special Requests Section */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">Special Requests</h3>
              <p className="text-gray-600 text-sm">
                Add any special requests or notes for the fair manager.
              </p>
              <textarea
                value={flow.special_requests}
                onChange={(e) => updateFlow({ special_requests: e.target.value })}
                maxLength={500}
                rows={4}
                placeholder="E.g., need extra space for display, arriving early for setup..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
              <p className="text-sm text-gray-400 text-right">
                {flow.special_requests.length}/500 characters
              </p>
            </div>
          </div>
        )}

        {/* Step 6: Review & Confirm */}
        {flow.step === 6 && (
          <ReservationSummary
            slot={slot}
            fair={fair}
            reservationType={flow.reservation_type!}
            selectedDates={flow.selected_dates}
            rentalItems={flow.rental_items}
            specialRequests={flow.special_requests}
            totalPrice={flow.total_price}
            discountCode={flow.discount_code} // Issue #375
            discountValid={flow.discount_valid} // Issue #375
            discountType={flow.discount_type} // Issue #375
            discountValue={flow.discount_value} // Issue #375
            originalPrice={flow.original_price} // Issue #375
            onEditType={() => updateFlow({ step: 2 })}
            onEditDates={() => updateFlow({ step: 3 })}
            onEditRentals={() => updateFlow({ step: 4 })}
          />
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <button
          onClick={handleBack}
          disabled={flow.step === 1 || isLoading}
          className="flex items-center gap-2 px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <button
          onClick={handleNext}
          disabled={
            isLoading ||
            (flow.step === 1 && !flow.vendor_id) ||
            (flow.step === 2 && !flow.reservation_type) ||
            (flow.step === 3 && flow.reservation_type === 'per_day' && flow.selected_dates.length === 0)
          }
          className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : flow.step === 6 ? (
            <>
              <Check className="w-4 h-4" />
              Confirm Reservation
            </>
          ) : (
            <>
              Next
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="exit-confirm-title"
            className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4"
          >
            <h3
              id="exit-confirm-title"
              className="text-lg font-semibold text-gray-900 mb-2"
            >
              Exit Reservation?
            </h3>
            <p className="text-gray-600 mb-6">
              Your progress will be lost. Are you sure you want to exit?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowExitConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Stay
              </button>
              <button
                onClick={confirmExit}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrap with providers
export default function ReservationWizard() {
  return (
    <ReservationProvider>
      <ReservationWizardContent />
    </ReservationProvider>
  );
}
